import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, CalendarCheck, User } from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import { Routes } from '@/routes/routeNames';
import { LuvelleWordmark } from '@/components/shared/LuvelleWordmark';
import { LanguageToggle } from '@/components/shared/LanguageToggle';
import { cn } from '@/lib/utils';

interface Tab {
  to: string;
  labelKey: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  end?: boolean;
}

const TABS: Tab[] = [
  { to: Routes.bookings.path, labelKey: 'nav.bookings', icon: CalendarCheck },
  // Home sits in the centre of the mobile bar as a raised brand FAB.
  { to: Routes.home.path, labelKey: 'nav.home', icon: Home, end: false },
  // Wallet tab hidden 2026-05-24 pre-M1 — i18n keys (57 wallet strings)
  // still pending AR linguist sign-off. Restore by re-adding the entry
  // and re-importing the Wallet icon from lucide-react.
  // { to: Routes.wallet.path, labelKey: 'nav.wallet', icon: Wallet },
  { to: Routes.account.path, labelKey: 'nav.account', icon: User },
];

/**
 * AppShell — responsive frame around the main tab pages.
 *
 * Layouts:
 *   - Desktop (≥md): top header with inline wordmark, horizontal nav,
 *                    language toggle. No bottom bar.
 *   - Mobile  (<md): top header with inline wordmark + language toggle.
 *                    Bottom tab bar pinned to the safe-area inset.
 *
 * Tab routes mount as children via React Router's Outlet, so a single
 * `<Route element={<AppShell />}>...</Route>` wraps all the tabs in
 * AppRouter — keeps route declarations terse and prevents the shell
 * from re-mounting on tab navigation.
 */
export function AppShell() {
  const { t } = useTranslation('common');
  const location = useLocation();

  // Hide the shell chrome on full-bleed pages (e.g. address picker).
  // Currently nothing in /home, /bookings, /wallet, /account qualifies —
  // kept here as a hook for future drawer/full-bleed flows.
  const chromeless = false;

  // The home route opens on a full-bleed hero banner. While the banner is in
  // view the header floats transparently over it (white wordmark); once the
  // banner scrolls away the header solidifies to a plain white bar. Other
  // routes always get the solid bar (and clear it with top padding).
  const isHome = location.pathname === Routes.home.path;
  // Immersive routes own a full-bleed hero with their own back affordance
  // (category page, service detail). The shell header is suppressed there on
  // mobile (and main needs no top padding); it returns on desktop.
  const isCategory = location.pathname.startsWith('/category');
  const isServiceDetail = /^\/services\/hourly\/[^/]+$/.test(location.pathname);
  // Account sub-pages (addresses, profile, support…) render their own back +
  // title header, so the shell's logo bar is redundant on mobile.
  const isAccountSub = location.pathname.startsWith('/account/');
  // The booking wizard is a self-contained flow with its own back + progress
  // header and a sticky next-step footer CTA. The shell's floating bottom nav
  // would otherwise overlap that footer on mobile, so we treat it as immersive.
  const isBookingWizard =
    location.pathname === '/book' ||
    /^\/services\/hourly\/[^/]+\/book$/.test(location.pathname);
  const immersive = isCategory || isServiceDetail || isAccountSub || isBookingWizard;
  const [scrolledPastHero, setScrolledPastHero] = useState(false);

  useEffect(() => {
    if (!isHome) {
      setScrolledPastHero(false);
      return;
    }
    const onScroll = () => setScrolledPastHero(window.scrollY > 200);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  const overHero = isHome && !scrolledPastHero;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      {/* ─── Top header ───────────────────────────────────────── */}
      {!chromeless && (
        <header
          className={cn(
            'fixed top-0 inset-x-0 z-40 safe-top transition-colors duration-300',
            // Immersive routes own the mobile header; the shell header returns
            // on desktop only (logo + nav, above the desktop hero).
            immersive && 'hidden md:block',
            overHero
              ? 'bg-transparent'
              : 'bg-background/90 backdrop-blur-md border-b border-border',
          )}
        >
          <div className="container mx-auto flex items-center justify-between h-14 md:h-20 gap-4">
            <NavLink
              to={Routes.home.path}
              className={cn(
                'transition-colors',
                overHero ? 'text-white drop-shadow-sm' : 'text-primary',
              )}
            >
              <LuvelleWordmark
                variant="inline"
                size="sm"
                showTagline={false}
                className="md:hidden"
              />
              <LuvelleWordmark
                variant="inline"
                size="md"
                showTagline={false}
                className="hidden md:flex"
              />
            </NavLink>

            {/* Desktop horizontal nav */}
            <nav className="hidden md:flex items-center gap-1" aria-label="primary">
              {TABS.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.end}
                  className={({ isActive }) =>
                    cn(
                      'inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : overHero
                          ? 'text-white/90 hover:bg-white/15 hover:text-white'
                          : 'text-charcoal-700 hover:bg-pink-50 hover:text-primary',
                    )
                  }
                >
                  <tab.icon className="h-4 w-4" aria-hidden />
                  <span>{t(tab.labelKey)}</span>
                </NavLink>
              ))}
            </nav>

            <LanguageToggle variant={overHero ? 'ghost' : 'outline'} />
          </div>
        </header>
      )}

      {/* ─── Main content ─────────────────────────────────────── */}
      <main
        className={cn(
          // Reserve room for the floating mobile tab bar (bar + margin + FAB).
          'flex-1 pb-28 md:pb-0',
          // The header is fixed and overlays content. The home route opens
          // under it (hero runs edge-to-edge to the very top). Immersive routes
          // own the header on mobile (no padding) but clear the shell header on
          // desktop. Every other route clears the fixed header.
          !isHome && (immersive ? 'md:pt-20' : 'pt-14 md:pt-20'),
        )}
      >
        <Outlet />
      </main>

      {/* ─── Mobile bottom tab bar ────────────────────────────── */}
      {/* Hidden during the booking wizard — that flow owns the bottom of the
       *  screen with its own sticky next-step CTA, like a focused checkout. */}
      {!chromeless && !isBookingWizard && (
        <nav
          aria-label="primary mobile"
          className="md:hidden fixed bottom-0 inset-x-0 z-50 safe-bottom pointer-events-none"
        >
          {/* Floating rounded bar — detached from the edges, elevated with a
           *  soft plum-tinted shadow (matches the reference app). */}
          <div className="pointer-events-auto mx-4 mb-3 rounded-[28px] bg-card shadow-[0_16px_42px_rgba(36,8,36,0.18)]">
            <ul className="flex items-stretch justify-around h-16 px-3">
              {TABS.map((tab) => {
                const isHome = tab.to === Routes.home.path;
                return (
                  <li key={tab.to} className="flex-1">
                    <NavLink
                      to={tab.to}
                      end={tab.end}
                      className="relative flex h-full flex-col items-center justify-center gap-1"
                    >
                      {({ isActive }) =>
                        isHome ? (
                          // Centre home — raised brand FAB (always prominent).
                          // The circle is absolutely positioned so it pops above
                          // the bar without shifting the label, which stays on
                          // the same baseline as the side-tab labels.
                          <>
                            <span className="absolute -top-7 left-1/2 -translate-x-1/2 grid h-14 w-14 place-items-center rounded-full bg-primary text-primary-foreground shadow-[0_10px_24px_rgba(36,8,36,0.35)] ring-4 ring-card transition-transform active:scale-95">
                              <tab.icon className="h-6 w-6" aria-hidden />
                            </span>
                            {/* Spacer matching the side-tab icon so the label
                             *  lines up with the others. */}
                            <span className="h-5 w-5" aria-hidden />
                            <span
                              className={cn(
                                'text-[10px] leading-none',
                                isActive ? 'font-bold text-primary' : 'font-medium text-charcoal-500',
                              )}
                            >
                              {t(tab.labelKey)}
                            </span>
                          </>
                        ) : (
                          <>
                            <tab.icon
                              className={cn(
                                'h-5 w-5 transition-colors',
                                isActive ? 'text-primary' : 'text-charcoal-400',
                              )}
                              aria-hidden
                            />
                            <span
                              className={cn(
                                'text-[10px] leading-none transition-colors',
                                isActive ? 'font-semibold text-primary' : 'font-medium text-charcoal-500',
                              )}
                            >
                              {t(tab.labelKey)}
                            </span>
                          </>
                        )
                      }
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        </nav>
      )}

      {/* Mark the current path on the root element so future skills can
       * style edge cases (e.g. bottom-bar shadow on long-scroll pages).
       * Kept off the DOM until needed. */}
      <span hidden data-path={location.pathname} />
    </div>
  );
}

export default AppShell;
